import { BRAND, type ColorMode } from '@chukua/common';
import type { ExpressionSpecification, FilterSpecification, LayerSpecification } from 'maplibre-gl';
import type { TruckMapStatus } from '../policies/truck-map-status';

export const TRUCKS_SOURCE_ID = 'live-trucks';
/** The layer a pointer can hover or click to reach one truck. */
export const TRUCK_HIT_LAYER_ID = 'live-truck-dots';
export const TRUCK_SELECTION_LAYER_ID = 'live-truck-selection';

/** Fonts the MapTiler streets style serves glyphs for; a symbol layer naming any other draws no text. */
const LABEL_FONT = ['Roboto Medium', 'Noto Sans Regular'];

/** Each status has its own palette colour, so the dot says it without the legend and without relying on shape alone (the panel says it in words). */
export function truckStatusColors(mode: ColorMode): Record<TruckMapStatus, string> {
  const palette = BRAND.palettes[mode];
  return { moving: palette.success, stopped: palette.info, late: palette.warning, silent: palette.textMuted };
}

function statusColorExpression(mode: ColorMode): ExpressionSpecification {
  const colors = truckStatusColors(mode);
  return ['match', ['get', 'status'], 'moving', colors.moving, 'stopped', colors.stopped, 'late', colors.late, colors.silent];
}

/** No truck is selected: a filter nothing satisfies. */
export function selectionFilter(truckId: string | null): FilterSpecification {
  return ['==', ['get', 'truckId'], truckId ?? ''];
}

/** A dot per truck coloured by status, an arrow on it where the fix has a heading, and a ring round the truck whose panel is open. */
export function truckLayers(mode: ColorMode): LayerSpecification[] {
  const palette = BRAND.palettes[mode];
  return [
    {
      id: TRUCK_SELECTION_LAYER_ID,
      type: 'circle',
      source: TRUCKS_SOURCE_ID,
      filter: selectionFilter(null),
      paint: { 'circle-radius': 14, 'circle-color': palette.primary, 'circle-opacity': 0.25, 'circle-stroke-color': palette.primary, 'circle-stroke-width': 2 },
    },
    {
      id: TRUCK_HIT_LAYER_ID,
      type: 'circle',
      source: TRUCKS_SOURCE_ID,
      paint: { 'circle-radius': 8, 'circle-color': statusColorExpression(mode), 'circle-stroke-color': palette.surface, 'circle-stroke-width': 2 },
    },
    {
      id: 'live-truck-headings',
      type: 'symbol',
      source: TRUCKS_SOURCE_ID,
      filter: ['get', 'hasHeading'],
      layout: {
        'text-field': '▲',
        'text-font': LABEL_FONT,
        'text-size': 9,
        'text-rotate': ['get', 'heading'],
        'text-rotation-alignment': 'map',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': palette.surface },
    },
  ];
}
